import { inject, Injectable } from '@angular/core';

import type { Observable } from 'rxjs';
import { map } from 'rxjs';

import { HttpApiService } from '@core/http-legacy';
import { type PaginationMeta } from '@core/http-types';
import type { ID } from '@core/ngrx-legacy';
import type {
  DataSource,
  DataSourceListItem,
  AnyOption,
  InputOptionsResponse,
  CreateViewPayload,
  FetchRecordsPayload,
  FilterPayload,
  LayoutRecordsCountPayload,
  UpdateViewPayload,
  View,
  ViewListItem,
  FetchGroupsPayload,
  GroupsQueryParams,
  GroupsResponse,
  ViewPositionPayload,
  RecordsResponse,
  ViewRecord,
  GroupId,
  AnyDataSourceName,
} from '@core/views-types';
import { getOptions } from '@core/walkthrough-util';

import { injectTellentViewsHttp } from './tellent-views.http';
import type { FilterGroupResponse } from '../store/filter-group/filter-group.store';
import type { RecordsQueryParams } from '../store/records/records.store';
import type { FetchSwimlanesPayload } from '../store/swimlanes/swimlanes.store';
import type { ViewResponse } from '../store/view/view.store';
import type { ViewCounter } from '../store/view-counters/view-counters.store';

@Injectable({ providedIn: 'root' })
export class ViewsHttpService {
  private readonly tellentViewsHttp = injectTellentViewsHttp();
  private readonly recruiteeViewHttp = inject(HttpApiService);

  public listDataSources(): Observable<DataSourceListItem[]> {
    return this.recruiteeViewHttp
      .get(`/views/data_sources`)
      .pipe(map(({ dataSources }) => dataSources));
  }

  public fetchDataSource(dataSourceId: string): Observable<DataSource> {
    return this.resolveHttp(dataSourceId)
      .get(`/views/data_sources/${dataSourceId}`)
      .pipe(map(({ dataSource }) => dataSource));
  }

  public fetchFilterGroup(
    dataSourceId: string,
    filterGroupId: string,
    queryParams: { searchQuery?: string; page?: number; limit?: number },
  ): Observable<FilterGroupResponse> {
    return this.resolveHttp(dataSourceId).get(
      `/views/${dataSourceId}/filter_groups/${filterGroupId}/filters`,
      {
        ...(queryParams.searchQuery && { search_query: queryParams.searchQuery }),
        ...(queryParams.page && { page: queryParams.page }),
        ...(queryParams.limit && { limit: queryParams.limit }),
      },
    );
  }

  public fetchInputOptions(
    dataSourceId: string,
    filterId: string,
    inputId: string,
    filterValue: FilterPayload | null,
    queryParams?: Partial<Omit<PaginationMeta, 'totalCount'> & { searchQuery: string }>,
  ): Observable<InputOptionsResponse<AnyOption>> {
    const { searchQuery, ...query } = queryParams ?? {};
    return this.resolveHttp(dataSourceId).post(
      `/views/${dataSourceId}/filters/${filterId}/inputs/${inputId}/options`,
      { ...query, search_query: searchQuery },
      filterValue ? { filters: [filterValue] } : null,
    );
  }

  public fetchViewsList(dataSourceId: string): Observable<ViewListItem[]> {
    return this.resolveHttp(dataSourceId)
      .get(`/views/${dataSourceId}/views`)
      .pipe(map(({ views }) => views));
  }

  public updateViewsPositions(
    dataSourceId: string,
    positions: ViewPositionPayload[],
  ): Observable<ViewListItem[]> {
    return this.resolveHttp(dataSourceId)
      .post(`/views/${dataSourceId}/update_positions`, null, { positions })
      .pipe(map(({ views }) => views));
  }

  public fetchView(dataSourceId: string, viewId: View['id']): Observable<ViewResponse> {
    return this.resolveHttp(dataSourceId).get(`/views/${dataSourceId}/views/${viewId}`);
  }

  public createView(dataSourceId: string, data: CreateViewPayload): Observable<ViewResponse> {
    return this.resolveHttp(dataSourceId).post(`/views/${dataSourceId}/views`, null, data);
  }

  public removeView(dataSourceId: string, viewId: number): Observable<ViewResponse> {
    return this.resolveHttp(dataSourceId).delete(`/views/${dataSourceId}/views/${viewId}`);
  }

  public updateView(
    dataSourceId: string,
    viewPayload: UpdateViewPayload,
  ): Observable<ViewResponse> {
    return this.resolveHttp(dataSourceId).patch(
      `/views/${dataSourceId}/views/${viewPayload.id}`,
      null,
      viewPayload,
    );
  }

  public fetchGroups(
    dataSourceId: string,
    data: FetchGroupsPayload,
    queryParams?: GroupsQueryParams,
  ): Observable<GroupsResponse> {
    const { searchQuery, ...query } = queryParams ?? {};
    return this.resolveHttp(dataSourceId).post(
      `/views/${dataSourceId}/groups`,
      { ...query, search_query: searchQuery },
      data,
    );
  }

  public fetchSwimlanes(
    dataSourceId: string,
    data: FetchSwimlanesPayload,
    queryParams?: GroupsQueryParams,
  ) {
    return this.resolveHttp(dataSourceId).post(
      `/views/${dataSourceId}/swimlanes`,
      queryParams,
      data,
    );
  }

  public fetchRecords(
    dataSourceId: string,
    data: FetchRecordsPayload,
    queryParams?: RecordsQueryParams,
  ): Observable<RecordsResponse> {
    return this.resolveHttp(dataSourceId).post(
      `/views/${dataSourceId}/records`,
      queryParams,
      data,
      getOptions({ hasOnboarding: true }),
    );
  }

  public fetchLayoutRecordsCount(
    dataSourceId: string,
    data: LayoutRecordsCountPayload,
    queryParams?: RecordsQueryParams,
  ): Observable<RecordsResponse> {
    return this.resolveHttp(dataSourceId).post(
      `/views/${dataSourceId}/records`,
      queryParams,
      data,
      getOptions({ hasOnboarding: true }),
    );
  }

  public fetchViewCounters(dataSourceId: string, ids: ID[]) {
    return this.resolveHttp(dataSourceId)
      .get(
        `/views/${dataSourceId}/counters`,
        { ids },
        {
          headers: {
            'X-Json-Accent': [],
          },
        },
      )
      .pipe(
        map(({ counters }) =>
          Object.entries(counters).map(
            ([id, count]) =>
              <ViewCounter>{
                id: Number.parseInt(id, 10) || id,
                count,
              },
          ),
        ),
      );
  }

  public saveSnapshot(
    dataSourceId: string,
    viewId: View['id'],
    codename: View['codename'],
    form: CreateViewPayload,
  ): Observable<void> {
    const id = codename ? { codename } : { viewId };
    return this.resolveHttp(dataSourceId).post(`/views/${dataSourceId}/views_snapshots`, null, {
      form,
      ...id,
    });
  }

  public clearSnapshot(
    dataSourceId: string,
    viewId: View['id'],
    codename: View['codename'],
  ): Observable<void> {
    const query = codename ? { codename } : { view_id: viewId };
    return this.resolveHttp(dataSourceId).delete(`/views/${dataSourceId}/views_snapshots`, query);
  }

  public moveRecord(
    dataSourceId: string,
    recordId: ViewRecord['id'],
    targetGroup: { configId: string; groupId: GroupId },
  ) {
    return this.resolveHttp(dataSourceId).patch(
      `/views/${dataSourceId}/records/${recordId}/move`,
      null,
      targetGroup,
    );
  }

  private resolveHttp(datasourceId: string) {
    const map: Record<AnyDataSourceName, HttpApiService> = {
      candidates: this.recruiteeViewHttp,
      jobs: this.recruiteeViewHttp,
      offer_templates: this.recruiteeViewHttp,
      ask_templates: this.tellentViewsHttp,
      ask_tickets: this.tellentViewsHttp,
    };
    return map[<AnyDataSourceName>datasourceId];
  }
}
